<!-- SpecGlossary — a spec label that explains itself.

     The label (Process, Variety…) is a button. Tapping it opens a small popover
     with one line on what the field means and one line for each piece of jargon
     in its value: "Washed", "SL28", "EA sugarcane". The value itself stays
     plain text beside it; the popover is for the person who has never heard
     the word, and costs nothing for the one who has. -->
<template>
  <button
    :id="triggerId"
    type="button"
    class="term"
    :aria-haspopup="'dialog'"
    :aria-expanded="open ? 'true' : 'false'"
    @click="show"
  >
    <span>{{ label }}</span>
    <IonIcon :icon="helpCircleOutline" aria-hidden="true" class="term__icon" />
  </button>

  <IonPopover
    :is-open="open"
    :event="evt"
    :keep-contents-mounted="false"
    side="bottom"
    alignment="start"
    class="gloss-pop"
    @did-dismiss="open = false"
  >
    <div class="gloss" role="document">
      <p class="gloss__line"><strong>{{ label }}</strong> {{ fieldLine }}</p>
      <p v-for="t in terms" :key="t.term" class="gloss__line">
        <strong>{{ t.term }}</strong> {{ t.line }}
      </p>
    </div>
  </IonPopover>
</template>

<script>
// The glossary. Keys are matched case-insensitively as whole words inside a
// spec value, longest first, so "Anaerobic Natural" explains both words and
// "Yellow Bourbon" wins over "Bourbon".
export const FIELD = {
  Origin: 'The country the coffee was grown in, and the region where the label says.',
  Process: 'How the fruit was taken off the seed after picking. It shapes the cup as much as the farm does.',
  Variety: 'The plant, like a grape variety in wine. Each one has its own flavour and yield.',
  Altitude: 'Metres above sea level (masl). Higher farms ripen cherries slowly, which tends to mean denser, brighter coffee.',
  Producer: 'The farmer, family, or washing station who grew and processed it.',
  Brew: 'What the roaster had in mind when they chose the roast.',
};

export const GLOSSARY = {
  // process
  washed: 'Fruit removed, then fermented and rinsed in water before drying. Clean and bright.',
  'fully washed': 'Fruit removed, then fermented and rinsed in water before drying. Clean and bright.',
  'special prep washed': 'A washed coffee given extra sorting and care. Clean, bright and very even.',
  natural: 'Dried whole in the cherry, fruit and all. Sweeter, heavier, often jammy or winey.',
  honey: 'Skin off, sticky fruit left on to dry. Sits between washed and natural: sweet and round.',
  'black honey': 'A honey process with most of the fruit left on and dried slowly. The sweetest, fruitiest honey.',
  anaerobic: 'Fermented in sealed tanks without oxygen. Bold, boozy, tropical flavours.',
  'carbonic maceration': 'Whole cherries fermented under CO₂, borrowed from winemaking. Vivid, winey fruit.',
  'wine process': 'Cherries fermented long in their own juice, like wine. Deep, boozy fruit.',
  mosto: 'Fermented with juice (mosto) pressed from other cherries. Intense and fruity.',
  mossto: 'Fermented with juice (mosto) pressed from other cherries. Intense and fruity.',
  'co-ferment': 'Fermented alongside added fruit, yeast or spice. Expect loud, unusual flavours.',
  yeast: 'Fermented with a chosen yeast strain for a cleaner, more controlled fruitiness.',
  lactobasillus: 'Fermented with lactic bacteria, like yoghurt. Creamy body, soft acidity.',
  lactobacillus: 'Fermented with lactic bacteria, like yoghurt. Creamy body, soft acidity.',
  hydro: 'The cherries are soaked in water before drying, for a cleaner, more even ferment.',
  // decaf
  decaf: 'Caffeine removed (about 99.9%) before roasting. Same coffee, no buzz.',
  'ea sugarcane': 'Decaf made with ethyl acetate from fermented sugarcane. Natural, gentle, keeps sweetness.',
  'sugar cane decaf': 'Decaf made with ethyl acetate from fermented sugarcane. Natural, gentle, keeps sweetness.',
  'ethyl acetate decaffeination': 'Decaf made with ethyl acetate, usually from sugarcane. Natural, gentle, keeps sweetness.',
  'ea decaf': 'Decaf made with ethyl acetate from fermented sugarcane. Natural, gentle, keeps sweetness.',
  'swiss water': 'Decaf made with water and carbon filters only. No solvents at all.',
  // variety
  heirloom: 'Ethiopia\'s thousands of wild and local varieties, grown mixed together. Floral and complex.',
  landrace: 'Old local varieties adapted to one place over generations.',
  sl28: 'A Kenyan classic bred in the 1930s. Juicy blackcurrant-like acidity.',
  sl34: 'SL28\'s Kenyan sibling, suited to wetter farms. Heavy body, bright acidity.',
  'ruiru 11': 'A disease-resistant Kenyan hybrid, often grown with SL28 and SL34.',
  batian: 'A newer Kenyan variety that resists disease and still tastes like the SLs.',
  bourbon: 'An old, sweet, low-yield variety from the island of Réunion.',
  'red bourbon': 'Bourbon with red cherries. The classic, sweet and balanced.',
  'yellow bourbon': 'A Bourbon that ripens yellow. Often extra sweet.',
  'pink bourbon': 'A rare Colombian variety with pink cherries. Floral and very sweet.',
  typica: 'One of the oldest cultivated varieties. Clean, sweet and delicate.',
  caturra: 'A compact natural mutation of Bourbon. Bright and easy-drinking.',
  caturron: 'A local Colombian selection related to Caturra.',
  catuai: 'A Brazilian cross of Caturra and Mundo Novo. Sturdy and dependable.',
  castillo: 'Colombia\'s own rust-resistant variety, grown on most of its farms.',
  colombia: 'Variedad Colombia: a rust-resistant hybrid bred in Colombia.',
  catimor: 'A hardy, rust-resistant hybrid of Caturra and Timor.',
  timor: 'A natural hybrid of arabica and robusta, the parent of many disease-resistant varieties.',
  'villa sarchi': 'A small Bourbon mutation from Costa Rica. Bright and elegant.',
  pache: 'A dwarf Typica mutation from Guatemala.',
  maragogype: 'A Typica mutation with unusually huge beans.',
  maracaturra: 'A cross of Maragogype and Caturra. Big beans, fruity cup.',
  obata: 'A Brazilian rust-resistant hybrid with a smooth, chocolatey cup.',
  'f1 hybrid': 'A first-generation cross bred for yield and disease resistance.',
  geisha: 'A prized Ethiopian-born variety. Jasmine, bergamot and tea-like.',
  gesha: 'A prized Ethiopian-born variety. Jasmine, bergamot and tea-like.',
  // brew
  filter: 'Roasted light for pour-over, batch brew and plunger.',
  omni: 'Roasted to work as espresso or filter.',
  espresso: 'Roasted for espresso machines and milk drinks.',
};

const KEYS = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length);
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The glossary lines for the jargon in a value, longest match first, no overlaps. */
export function termsIn (value) {
  let rest = ` ${String(value || '').toLowerCase()} `;
  const found = [];
  for (const k of KEYS) {
    const re = new RegExp(`(^|[^a-z0-9])${esc(k)}(?=[^a-z0-9]|$)`);
    if (!re.test(rest)) continue;
    const line = GLOSSARY[k];
    if (!found.some((f) => f.line === line)) found.push({ term: titleCase(k), line });
    rest = rest.replace(new RegExp(esc(k), 'g'), ' ');
  }
  return found;
}

const DISPLAY = { sl28: 'SL28', sl34: 'SL34', 'f1 hybrid': 'F1 hybrid', 'ea sugarcane': 'EA sugarcane', 'ea decaf': 'EA decaf', 'ruiru 11': 'Ruiru 11' };
function titleCase (k) {
  return DISPLAY[k] || k.charAt(0).toUpperCase() + k.slice(1);
}
</script>

<script setup>
import { ref, computed } from 'vue';
import { IonIcon, IonPopover } from '@ionic/vue';
import { helpCircleOutline } from 'ionicons/icons';

const props = defineProps({
  /** The field's name: Origin, Process, Variety, Altitude, Producer, Brew. */
  label: { type: String, required: true },
  /** The field's value, scanned for jargon. */
  value: { type: String, default: '' },
});

const triggerId = `gloss-${Math.random().toString(36).slice(2, 9)}`;

const open = ref(false);
const evt = ref(null);
const fieldLine = computed(() => FIELD[props.label] || '');
const terms = computed(() => termsIn(props.value));

function show (e) {
  evt.value = e;
  open.value = true;
}
</script>

<style scoped>
/* The spec's label, in the mono of the price clips; the ? says it explains itself. */
.term {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  min-height: var(--tap);
  margin: calc(var(--sp-3) * -1) 0 calc(var(--sp-3) * -1);
  padding: 0;
  border: 0;
  background: none;
  color: var(--muted, #5E6B60);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.term:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; border-radius: var(--radius-sm); }
.term__icon { font-size: var(--text-sm); color: var(--faint, #8A948B); transition: color var(--dur) var(--ease); }
.term:hover .term__icon { color: var(--ink, #1F2A22); }

.gloss {
  display: grid;
  gap: var(--sp-2);
  padding: var(--sp-4);
  color: var(--ink-2, #3E4A41);
  font-size: var(--text-sm);
  line-height: 1.5;
}
.gloss__line { margin: 0; }
.gloss__line strong { color: var(--ink, #1F2A22); font-weight: 800; }
</style>

<style>
/* Popovers render at the app root, outside the scoped tree. */
ion-popover.gloss-pop {
  --width: min(20rem, calc(100vw - var(--sp-6)));
  --border-radius: var(--radius);
  --background: var(--dock, #FAF7F1);
  --box-shadow: 0 12px 32px rgba(40, 30, 20, 0.18), 0 0 0 1px var(--line, #ECE6DC);
}
</style>
